// §11.8 — the document layer: objects → pages → positioned text.
//
// Reading strategy (D-170): the cross-reference table is **not** trusted or even read. Every
// `N G obj … endobj` in the file is scanned for directly, with a later definition beating an
// earlier one — which is exactly what an incremental update means — and object streams are
// expanded afterwards to fill in what PDF 1.5+ compressed away. A great many PDFs in the wild
// have a broken xref and open fine everywhere because every serious reader falls back to this;
// starting here simply removes the fallback path.
import {
  decodeStream,
  dictOf,
  get,
  indexOfBytes,
  isArray,
  isDict,
  isName,
  isRef,
  isStr,
  isStream,
  latin1,
  bytesOf,
  matchesAt,
  PdfLexer,
  UnsupportedFilter,
  type PdfDict,
  type PdfStream,
  type PdfValue,
} from './objects';
import {
  PDF_DEFAULT_GLYPH_WIDTH,
  PDF_FORM_DEPTH_LIMIT,
  PDF_MAX_PAGES,
  PDF_MONOSPACE_GLYPH_WIDTH,
} from '@/config';

export interface PdfFontStyle {
  readonly family: string;
  readonly bold: boolean;
  readonly italic: boolean;
}

/** One show-operation's worth of text, already placed in top-left page pixels at 100 %. */
export interface PdfTextRun {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  /** Radians, clockwise. Zero for all but rotated headings and watermarks. */
  readonly angle: number;
  readonly style: PdfFontStyle;
}

export interface PdfPage {
  readonly index: number;
  readonly width: number;
  readonly height: number;
  readonly runs: readonly PdfTextRun[];
  /** Every run's text joined — what §11.8's search runs over. */
  readonly text: string;
}

export interface PdfDocument {
  readonly pageCount: number;
  readonly title: string | null;
  readonly pages: readonly PdfPage[];
  /** Set when the file is encrypted or uses a filter this reader does not implement. */
  readonly limitation: string | null;
}

export class PdfParseError extends Error {}

type Matrix = readonly [number, number, number, number, number, number];

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

/** `a × b` in PDF's row-vector convention: apply `a` first, then `b`. */
function multiply(a: Matrix, b: Matrix): Matrix {
  return [
    a[0] * b[0] + a[1] * b[2],
    a[0] * b[1] + a[1] * b[3],
    a[2] * b[0] + a[3] * b[2],
    a[2] * b[1] + a[3] * b[3],
    a[4] * b[0] + a[5] * b[2] + b[4],
    a[4] * b[1] + a[5] * b[3] + b[5],
  ];
}

const OBJ = bytesOf('obj');
const TRAILER = bytesOf('trailer');

interface Font {
  readonly twoByte: boolean;
  readonly toUnicode: ReadonlyMap<number, string> | null;
  readonly widths: ReadonlyMap<number, number>;
  readonly defaultWidth: number;
  readonly style: PdfFontStyle;
}

const FALLBACK_FONT: Font = {
  twoByte: false,
  toUnicode: null,
  widths: new Map(),
  defaultWidth: PDF_DEFAULT_GLYPH_WIDTH,
  style: { family: 'sans-serif', bold: false, italic: false },
};

/**
 * The parsed file: every object, resolvable.
 *
 * Kept as a class because resolution is recursive — a page's `/Resources` may be a reference to a
 * dictionary whose `/Font` is a reference to a dictionary — and every reader of the tree needs the
 * same `resolve`.
 */
class PdfFile {
  private readonly objects = new Map<number, PdfValue>();
  private readonly resolving = new Set<number>();
  limitation: string | null = null;

  constructor(readonly bytes: Uint8Array) {
    const offsets = this.scanOffsets();
    for (const [num, offset] of offsets) {
      const lexer = new PdfLexer(bytes);
      lexer.pos = offset;
      const value = lexer.next((ref) => this.lengthAt(offsets.get(ref.num)));
      if (value !== undefined) this.objects.set(num, value);
    }
    this.expandObjectStreams();
  }

  /** Every `N G obj`, in file order — so a later definition simply overwrites an earlier one. */
  private scanOffsets(): Map<number, number> {
    const offsets = new Map<number, number>();
    const { bytes } = this;
    for (let index = 0; index + 3 <= bytes.length; index += 1) {
      if (!matchesAt(bytes, index, OBJ)) continue;
      // `obj` must be a whole token, not the tail of `endobj`.
      const after = bytes[index + 3];
      if (after !== undefined && after > 0x20 && after !== 0x3c && after !== 0x5b && after !== 0x2f) {
        continue;
      }
      const header = latin1(bytes.subarray(Math.max(0, index - 24), index));
      const match = /(\d+)\s+(\d+)\s+$/.exec(header);
      if (!match) continue;
      const num = Number.parseInt(match[1] ?? '', 10);
      if (!Number.isFinite(num)) continue;
      offsets.set(num, index + 3);
    }
    return offsets;
  }

  /** A `/Length` that is itself an indirect object. Parsed alone, with no resolver of its own. */
  private lengthAt(offset: number | undefined): number | undefined {
    if (offset === undefined) return undefined;
    const lexer = new PdfLexer(this.bytes);
    lexer.pos = offset;
    const value = lexer.next();
    return typeof value === 'number' ? value : undefined;
  }

  /** PDF 1.5+ packs most non-stream objects into `/Type /ObjStm`. Without this there are no pages. */
  private expandObjectStreams(): void {
    for (const value of [...this.objects.values()]) {
      if (!isStream(value)) continue;
      const type = value.dict.entries.get('Type');
      if (!isName(type) || type.name !== 'ObjStm') continue;

      let data: Uint8Array;
      try {
        data = this.decode(value);
      } catch (error) {
        if (error instanceof UnsupportedFilter) this.limitation = error.message;
        continue;
      }
      const count = this.number(value.dict.entries.get('N'), 0);
      const first = this.number(value.dict.entries.get('First'), 0);
      const header = new PdfLexer(data.subarray(0, first));
      const pairs: Array<[number, number]> = [];
      for (let index = 0; index < count; index += 1) {
        const num = header.next();
        const offset = header.next();
        if (typeof num !== 'number' || typeof offset !== 'number') break;
        pairs.push([num, offset]);
      }
      for (const [num, offset] of pairs) {
        // A top-level definition of the same object wins: it is the newer one by construction.
        if (this.objects.has(num)) continue;
        const lexer = new PdfLexer(data);
        lexer.pos = first + offset;
        const parsed = lexer.next();
        if (parsed !== undefined) this.objects.set(num, parsed);
      }
    }
  }

  resolve(value: PdfValue | undefined): PdfValue | undefined {
    let current = value;
    // A reference chain is legal; a reference *cycle* is not, and `resolving` is what makes the
    // difference between reading a broken file and hanging on one.
    for (let hops = 0; hops < 32; hops += 1) {
      if (!isRef(current)) return current;
      if (this.resolving.has(current.num)) return undefined;
      this.resolving.add(current.num);
      const next = this.objects.get(current.num);
      this.resolving.delete(current.num);
      current = next;
    }
    return undefined;
  }

  at(value: PdfValue | undefined, key: string): PdfValue | undefined {
    return this.resolve(get(this.resolve(value), key));
  }

  number(value: PdfValue | undefined, fallback: number): number {
    const resolved = this.resolve(value);
    return typeof resolved === 'number' ? resolved : fallback;
  }

  decode(stream: PdfStream): Uint8Array {
    return decodeStream(stream, (value) => this.resolve(value));
  }

  /** `/Root`, found without the xref: the trailer if there is one, the xref stream if not. */
  catalog(): PdfDict | undefined {
    let found: PdfValue | undefined;
    let from = 0;
    for (;;) {
      const index = indexOfBytes(this.bytes, TRAILER, from);
      if (index < 0) break;
      const lexer = new PdfLexer(this.bytes);
      lexer.pos = index + TRAILER.length;
      const dict = lexer.next();
      const root = this.at(dict, 'Root');
      if (isDict(root)) found = root;
      if (get(dict, 'Encrypt') !== undefined) this.limitation = 'encrypted';
      from = index + TRAILER.length;
    }
    if (isDict(found)) return found;

    for (const value of this.objects.values()) {
      const type = dictOf(value)?.entries.get('Type');
      if (isName(type) && type.name === 'XRef') {
        if (get(value, 'Encrypt') !== undefined) this.limitation = 'encrypted';
        const root = this.at(value, 'Root');
        if (isDict(root)) return root;
      }
    }
    // Last resort: the catalog itself, wherever it is.
    for (const value of this.objects.values()) {
      const type = dictOf(value)?.entries.get('Type');
      if (isName(type) && type.name === 'Catalog' && isDict(value)) return value;
    }
    return undefined;
  }

  /** Every page dictionary, with `/Resources`, `/MediaBox` and `/Rotate` inherited as §7.7.3.3 says. */
  pages(): Array<{ dict: PdfDict; inherited: Map<string, PdfValue> }> {
    const out: Array<{ dict: PdfDict; inherited: Map<string, PdfValue> }> = [];
    const root = this.catalog();
    const tree = this.at(root, 'Pages');
    const seen = new Set<PdfValue>();

    const walk = (node: PdfValue | undefined, inherited: Map<string, PdfValue>): void => {
      const dict = this.resolve(node);
      if (!isDict(dict) || seen.has(dict) || out.length >= PDF_MAX_PAGES) return;
      seen.add(dict);

      const next = new Map(inherited);
      for (const key of ['Resources', 'MediaBox', 'CropBox', 'Rotate']) {
        const value = dict.entries.get(key);
        if (value !== undefined) next.set(key, value);
      }

      const kids = this.resolve(dict.entries.get('Kids'));
      const type = dict.entries.get('Type');
      if (isArray(kids) && !(isName(type) && type.name === 'Page')) {
        for (const kid of kids.items) walk(kid, next);
        return;
      }
      out.push({ dict, inherited: next });
    };

    if (isDict(this.resolve(tree))) walk(tree, new Map());
    if (out.length === 0) {
      // A file whose page tree is unreachable still has its page objects; find them directly.
      for (const value of this.objects.values()) {
        const type = dictOf(value)?.entries.get('Type');
        if (isName(type) && type.name === 'Page' && isDict(value)) {
          out.push({ dict: value, inherited: new Map(value.entries) });
        }
        if (out.length >= PDF_MAX_PAGES) break;
      }
    }
    return out;
  }

  /** `/Info`'s `/Title`, if the document has one. Identified by shape: a `/Type` means a page. */
  title(): string | null {
    for (const value of this.objects.values()) {
      const dict = dictOf(value);
      if (!dict || dict.entries.get('Type') !== undefined) continue;
      const title = this.resolve(dict.entries.get('Title'));
      if (!isStr(title)) continue;
      const text = decodeTextString(title.bytes).trim();
      if (text.length > 0) return text;
    }
    return null;
  }

  font(dict: PdfValue | undefined): Font {
    const base = this.at(dict, 'BaseFont');
    const baseName = isName(base) ? base.name : '';
    const style = styleOf(baseName, this.number(this.at(this.at(dict, 'FontDescriptor'), 'Flags'), 0));

    const subtype = this.at(dict, 'Subtype');
    const isType0 = isName(subtype) && subtype.name === 'Type0';
    const encoding = this.at(dict, 'Encoding');
    const twoByte = isType0 && (!isName(encoding) || /Identity|UCS2|UTF16/.test(encoding.name));

    const toUnicodeStream = this.at(dict, 'ToUnicode');
    let toUnicode: Map<number, string> | null = null;
    if (isStream(toUnicodeStream)) {
      try {
        toUnicode = parseToUnicode(this.decode(toUnicodeStream));
      } catch {
        toUnicode = null;
      }
    }

    const widths = new Map<number, number>();
    let defaultWidth = /Courier|Mono/i.test(baseName)
      ? PDF_MONOSPACE_GLYPH_WIDTH
      : PDF_DEFAULT_GLYPH_WIDTH;

    if (isType0) {
      const descendants = this.at(dict, 'DescendantFonts');
      const descendant = isArray(descendants) ? this.resolve(descendants.items[0]) : undefined;
      defaultWidth = this.number(this.at(descendant, 'DW'), 1000);
      const list = this.at(descendant, 'W');
      if (isArray(list)) readCidWidths(list.items.map((item) => this.resolve(item)), widths);
    } else {
      const first = this.number(this.at(dict, 'FirstChar'), 0);
      const list = this.at(dict, 'Widths');
      if (isArray(list)) {
        list.items.forEach((item, index) => {
          const width = this.number(item, Number.NaN);
          if (Number.isFinite(width) && width > 0) widths.set(first + index, width);
        });
      }
      const missing = this.number(this.at(this.at(dict, 'FontDescriptor'), 'MissingWidth'), Number.NaN);
      if (Number.isFinite(missing) && missing > 0) defaultWidth = missing;
    }

    return { twoByte, toUnicode, widths, defaultWidth, style };
  }
}

function styleOf(baseName: string, flags: number): PdfFontStyle {
  const family = /Courier|Mono/i.test(baseName)
    ? 'monospace'
    : /Times|Serif|Georgia|Roman|Garamond|Book/i.test(baseName)
      ? 'serif'
      : 'sans-serif';
  return {
    family,
    bold: /Bold|Black|Heavy|Semibold/i.test(baseName) || (flags & (1 << 18)) !== 0,
    italic: /Italic|Oblique/i.test(baseName) || (flags & (1 << 6)) !== 0,
  };
}

/** `/W` is `[ c [w w w] cFirst cLast w … ]` — two shapes in one array. */
function readCidWidths(items: ReadonlyArray<PdfValue | undefined>, into: Map<number, number>): void {
  let index = 0;
  while (index < items.length) {
    const start = items[index];
    if (typeof start !== 'number') break;
    const second = items[index + 1];
    if (isArray(second)) {
      second.items.forEach((width, offset) => {
        if (typeof width === 'number') into.set(start + offset, width);
      });
      index += 2;
      continue;
    }
    const width = items[index + 2];
    if (typeof second !== 'number' || typeof width !== 'number') break;
    // A run of thousands of identical widths is legal and common; cap what is materialised.
    const last = Math.min(second, start + 65_535);
    for (let code = start; code <= last; code += 1) into.set(code, width);
    index += 3;
  }
}

/** A PDF text string: UTF-16BE when it carries the BOM, PDFDocEncoding (≈ Latin-1) otherwise. */
export function decodeTextString(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    let out = '';
    for (let index = 2; index + 1 < bytes.length; index += 2) {
      out += String.fromCharCode(((bytes[index] ?? 0) << 8) | (bytes[index + 1] ?? 0));
    }
    return out;
  }
  return latin1(bytes);
}

/** `bfchar` and `bfrange` out of a `/ToUnicode` CMap — the only reliable code → text mapping. */
export function parseToUnicode(bytes: Uint8Array): Map<number, string> {
  const map = new Map<number, string>();
  const lexer = new PdfLexer(bytes);
  const pending: PdfValue[] = [];

  const hexNumber = (value: PdfValue | undefined): number | null => {
    if (!isStr(value)) return null;
    let out = 0;
    for (const byte of value.bytes) out = (out << 8) | byte;
    return out;
  };
  const hexText = (value: PdfValue | undefined): string | null => {
    if (!isStr(value)) return null;
    let out = '';
    for (let index = 0; index + 1 < value.bytes.length; index += 2) {
      out += String.fromCharCode(((value.bytes[index] ?? 0) << 8) | (value.bytes[index + 1] ?? 0));
    }
    if (value.bytes.length === 1) out = String.fromCharCode(value.bytes[0] ?? 0);
    return out;
  };

  for (;;) {
    const token = lexer.next();
    if (token === undefined) break;
    if (typeof token === 'object' && token !== null && token.kind === 'op') {
      if (token.op === 'endbfchar') {
        for (let index = 0; index + 1 < pending.length; index += 2) {
          const code = hexNumber(pending[index]);
          const text = hexText(pending[index + 1]);
          if (code !== null && text !== null) map.set(code, text);
        }
        pending.length = 0;
        continue;
      }
      if (token.op === 'endbfrange') {
        for (let index = 0; index + 2 < pending.length; index += 3) {
          const from = hexNumber(pending[index]);
          const to = hexNumber(pending[index + 1]);
          const target = pending[index + 2];
          if (from === null || to === null || to < from || to - from > 65_535) continue;
          if (isArray(target)) {
            target.items.forEach((item, offset) => {
              const text = hexText(item);
              if (text !== null) map.set(from + offset, text);
            });
            continue;
          }
          const text = hexText(target);
          if (text === null || text.length === 0) continue;
          const tail = text.charCodeAt(text.length - 1);
          for (let code = from; code <= to; code += 1) {
            map.set(code, text.slice(0, -1) + String.fromCharCode(tail + (code - from)));
          }
        }
        pending.length = 0;
        continue;
      }
      // `beginbfchar` / `beginbfrange` and every other keyword resets the operand run.
      pending.length = 0;
      continue;
    }
    pending.push(token);
    if (pending.length > 3000) pending.splice(0, pending.length - 3000);
  }
  return map;
}

interface TextState {
  font: Font;
  fontSize: number;
  charSpacing: number;
  wordSpacing: number;
  hScale: number;
  leading: number;
  rise: number;
}

const EI = bytesOf('EI');

/**
 * One page's content streams, interpreted for text.
 *
 * Everything that is not text is skipped rather than approximated — a path operator moves no text
 * cursor, so ignoring it costs nothing in placement, and drawing a grey box where a photograph
 * belongs would be a lie about what was read.
 */
function extractRuns(
  file: PdfFile,
  content: Uint8Array,
  resources: PdfValue | undefined,
  base: Matrix,
  depth: number,
): PdfTextRun[] {
  const runs: PdfTextRun[] = [];
  const lexer = new PdfLexer(content);
  const operands: PdfValue[] = [];

  let ctm: Matrix = IDENTITY;
  const stack: Matrix[] = [];
  let tm: Matrix = IDENTITY;
  let tlm: Matrix = IDENTITY;

  const fontCache = new Map<string, Font>();
  let state: TextState = {
    font: FALLBACK_FONT,
    fontSize: 0,
    charSpacing: 0,
    wordSpacing: 0,
    hScale: 1,
    leading: 0,
    rise: 0,
  };
  const stateStack: TextState[] = [];

  const num = (index: number): number => {
    const value = operands[operands.length - index];
    return typeof value === 'number' ? value : 0;
  };

  const show = (bytes: Uint8Array): void => {
    const { font, fontSize } = state;
    const step = font.twoByte ? 2 : 1;
    let text = '';
    let width = 0;

    for (let index = 0; index + step <= bytes.length; index += step) {
      const code = step === 2 ? ((bytes[index] ?? 0) << 8) | (bytes[index + 1] ?? 0) : (bytes[index] ?? 0);
      text += font.toUnicode?.get(code) ?? (step === 2 ? '' : String.fromCharCode(code));
      const glyph = (font.widths.get(code) ?? font.defaultWidth) / 1000;
      width += glyph * fontSize + state.charSpacing + (code === 32 && step === 1 ? state.wordSpacing : 0);
    }

    if (text.trim().length > 0) {
      const trm = multiply(multiply([fontSize * state.hScale, 0, 0, fontSize, 0, state.rise], tm), multiply(ctm, base));
      const scale = Math.sqrt(Math.abs(trm[0] * trm[3] - trm[1] * trm[2]));
      if (scale > 0.01) {
        runs.push({
          text,
          x: trm[4],
          y: trm[5],
          size: scale,
          angle: Math.atan2(trm[1], trm[0]),
          style: state.font.style,
        });
      }
    }
    tm = multiply([1, 0, 0, 1, width * state.hScale, 0], tm);
  };

  const nextLine = (tx: number, ty: number): void => {
    tlm = multiply([1, 0, 0, 1, tx, ty], tlm);
    tm = tlm;
  };

  for (;;) {
    lexer.skipSpace();
    if (lexer.done) break;
    const token = lexer.next();
    if (token === undefined) break;
    if (typeof token !== 'object' || token === null || token.kind !== 'op') {
      operands.push(token);
      if (operands.length > 64) operands.splice(0, operands.length - 64);
      continue;
    }

    switch (token.op) {
      case 'q':
        stack.push(ctm);
        stateStack.push({ ...state });
        break;
      case 'Q': {
        ctm = stack.pop() ?? ctm;
        state = stateStack.pop() ?? state;
        break;
      }
      case 'cm':
        ctm = multiply([num(6), num(5), num(4), num(3), num(2), num(1)], ctm);
        break;
      case 'BT':
        tm = IDENTITY;
        tlm = IDENTITY;
        break;
      case 'Tf': {
        const size = num(1);
        const key = operands[operands.length - 2];
        state = { ...state, fontSize: size };
        if (isName(key)) {
          let font = fontCache.get(key.name);
          if (!font) {
            font = file.font(file.at(file.at(resources, 'Font'), key.name));
            fontCache.set(key.name, font);
          }
          state = { ...state, font };
        }
        break;
      }
      case 'Td':
        nextLine(num(2), num(1));
        break;
      case 'TD':
        state = { ...state, leading: -num(1) };
        nextLine(num(2), num(1));
        break;
      case 'Tm':
        tlm = [num(6), num(5), num(4), num(3), num(2), num(1)];
        tm = tlm;
        break;
      case 'T*':
        nextLine(0, -state.leading);
        break;
      case 'TL':
        state = { ...state, leading: num(1) };
        break;
      case 'Tc':
        state = { ...state, charSpacing: num(1) };
        break;
      case 'Tw':
        state = { ...state, wordSpacing: num(1) };
        break;
      case 'Tz':
        state = { ...state, hScale: num(1) / 100 };
        break;
      case 'Ts':
        state = { ...state, rise: num(1) };
        break;
      case 'Tj': {
        const value = operands[operands.length - 1];
        if (isStr(value)) show(value.bytes);
        break;
      }
      case "'": {
        nextLine(0, -state.leading);
        const value = operands[operands.length - 1];
        if (isStr(value)) show(value.bytes);
        break;
      }
      case '"': {
        state = { ...state, wordSpacing: num(3), charSpacing: num(2) };
        nextLine(0, -state.leading);
        const value = operands[operands.length - 1];
        if (isStr(value)) show(value.bytes);
        break;
      }
      case 'TJ': {
        const value = operands[operands.length - 1];
        if (isArray(value)) {
          for (const item of value.items) {
            if (isStr(item)) show(item.bytes);
            else if (typeof item === 'number') {
              const shift = (-item / 1000) * state.fontSize * state.hScale;
              tm = multiply([1, 0, 0, 1, shift, 0], tm);
            }
          }
        }
        break;
      }
      case 'Do': {
        // A form XObject is a page inside a page — headers, footers and whole templates live in
        // them, so skipping them loses text that is plainly on the page.
        const key = operands[operands.length - 1];
        if (!isName(key) || depth >= PDF_FORM_DEPTH_LIMIT) break;
        const form = file.at(file.at(resources, 'XObject'), key.name);
        const subtype = file.at(form, 'Subtype');
        if (!isStream(form) || !isName(subtype) || subtype.name !== 'Form') break;
        const matrix = file.at(form, 'Matrix');
        const own: Matrix = isArray(matrix) && matrix.items.length === 6
          ? (matrix.items.map((item) => file.number(item, 0)) as unknown as Matrix)
          : IDENTITY;
        try {
          runs.push(
            ...extractRuns(
              file,
              file.decode(form),
              file.at(form, 'Resources') ?? resources,
              multiply(multiply(own, ctm), base),
              depth + 1,
            ),
          );
        } catch {
          // A form that will not decode costs its own content, not the page's.
        }
        break;
      }
      case 'BI': {
        // Inline image data is raw bytes in the middle of the operator stream. Lexing it produces
        // nonsense operators, so it is skipped wholesale.
        const end = indexOfBytes(content, EI, lexer.pos);
        lexer.pos = end < 0 ? content.length : end + EI.length;
        break;
      }
      default:
        break;
    }
    operands.length = 0;
  }

  return runs;
}

function rectOf(file: PdfFile, value: PdfValue | undefined): [number, number, number, number] | null {
  const box = file.resolve(value);
  if (!isArray(box) || box.items.length < 4) return null;
  const numbers = box.items.slice(0, 4).map((item) => file.number(item, Number.NaN));
  if (numbers.some((entry) => !Number.isFinite(entry))) return null;
  const [a = 0, b = 0, c = 0, d = 0] = numbers;
  return [Math.min(a, c), Math.min(b, d), Math.max(a, c), Math.max(b, d)];
}

/** The page's own flip-and-rotate: PDF user space (bottom-left, `/Rotate`) → top-left pixels. */
export function pageTransform(
  box: readonly [number, number, number, number],
  rotate: number,
): { matrix: Matrix; width: number; height: number } {
  const [x0, y0, x1, y1] = box;
  const width = x1 - x0;
  const height = y1 - y0;
  const turn = (((Math.round(rotate / 90) * 90) % 360) + 360) % 360;
  if (turn === 90) return { matrix: [0, 1, 1, 0, -y0, -x0], width: height, height: width };
  if (turn === 180) return { matrix: [-1, 0, 0, 1, x1, -y0], width, height };
  if (turn === 270) return { matrix: [0, -1, -1, 0, y1, x1], width: height, height: width };
  return { matrix: [1, 0, 0, -1, -x0, y1], width, height };
}

export function parsePdf(bytes: Uint8Array): PdfDocument {
  if (indexOfBytes(bytes.subarray(0, 1024), bytesOf('%PDF-')) < 0) {
    throw new PdfParseError('not a PDF');
  }
  const file = new PdfFile(bytes);
  const pageDicts = file.pages();
  const pages: PdfPage[] = [];

  pageDicts.forEach((entry, index) => {
    const box =
      rectOf(file, entry.dict.entries.get('MediaBox') ?? entry.inherited.get('MediaBox')) ??
      [0, 0, 612, 792];
    const rotate = file.number(entry.dict.entries.get('Rotate') ?? entry.inherited.get('Rotate'), 0);
    const { matrix, width, height } = pageTransform(box, rotate);
    const resources =
      file.resolve(entry.dict.entries.get('Resources') ?? entry.inherited.get('Resources'));

    const parts: Uint8Array[] = [];
    const contents = file.resolve(entry.dict.entries.get('Contents'));
    const streams = isArray(contents) ? contents.items.map((item) => file.resolve(item)) : [contents];
    for (const stream of streams) {
      if (!isStream(stream)) continue;
      try {
        parts.push(file.decode(stream));
      } catch (error) {
        if (error instanceof UnsupportedFilter) file.limitation ??= error.message;
      }
    }

    const total = parts.reduce((sum, part) => sum + part.length + 1, 0);
    const content = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
      content.set(part, offset);
      offset += part.length;
      content[offset] = 0x0a;
      offset += 1;
    }

    let runs: PdfTextRun[] = [];
    try {
      runs = extractRuns(file, content, resources, matrix, 0);
    } catch {
      runs = [];
    }
    pages.push({
      index,
      width,
      height,
      runs,
      text: runs.map((run) => run.text).join(' '),
    });
  });

  return {
    pageCount: pages.length,
    title: file.title(),
    pages,
    limitation: file.limitation,
  };
}
